/**
 * Status pill colors for the notification stage-change badges.
 *
 * Reuses the app's existing status color scheme (same as the request board) so
 * notifications stay consistent with the rest of the app. The visual treatment
 * (pill shape, 1px colored stroke, tinted bg, colored text) matches the
 * Notifications.pen design; only the palette is the app's current one.
 *
 * If an optional explicit hex color is provided (e.g. a custom per-project
 * status color from the API), it is used to derive the pill instead of the
 * name-based mapping.
 */

export interface StatusPillStyle {
  color: string;
  'border-color': string;
  'background-color': string;
}

interface PillColors {
  text: string;
  bg: string;
}

// App status palette (mirrors project-requests `.status-*` classes).
const STATUS_COLORS: Record<string, PillColors> = {
  'to-do': { text: '#6B6B6B', bg: '#F5F5F5' },
  'todo': { text: '#6B6B6B', bg: '#F5F5F5' },
  'in-progress': { text: '#3B6FCE', bg: 'rgba(59, 111, 206, 0.08)' }, // $info
  'sent-to-approval': { text: '#5F42D9', bg: 'rgba(95, 66, 217, 0.08)' }, // $primary
  'sent-for-approval': { text: '#5F42D9', bg: 'rgba(95, 66, 217, 0.08)' },
  'on-going': { text: '#5F42D9', bg: 'rgba(95, 66, 217, 0.08)' },
  'completed': { text: '#249C00', bg: '#E8F5E9' }, // $success
  'on-hold': { text: '#C30000', bg: 'rgba(195, 0, 0, 0.08)' }, // $danger
};

const FALLBACK: PillColors = { text: '#4C4B4B', bg: '#F8F9FA' };

function normalize(name: string): string {
  return (name || '').trim().toLowerCase().replace(/\s+/g, '-');
}

/** Convert a 3/6-digit hex color to an rgba() string with the given alpha. */
function hexToRgba(hex: string, alpha: number): string {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Returns an ngStyle object for a status pill given the status name and an
 * optional explicit hex color override.
 */
export function getStatusPillStyle(statusName?: string | null, explicitColor?: string | null): StatusPillStyle {
  if (explicitColor) {
    return {
      color: explicitColor,
      'border-color': explicitColor,
      'background-color': hexToRgba(explicitColor, 0.08),
    };
  }

  const colors = STATUS_COLORS[normalize(statusName ?? '')] ?? FALLBACK;
  return {
    color: colors.text,
    'border-color': colors.text,
    'background-color': colors.bg,
  };
}
